import React, { useState } from 'react';
import { Users, BookOpen, CheckSquare, Plus, Sparkles, CheckCircle2, Play, Pause } from 'lucide-react';

export const TeacherClassroomModule: React.FC = () => {
  const [lessonTopic, setLessonTopic] = useState('Sensory Science Exploration');
  const [accommodations, setAccommodations] = useState([
    { id: 1, text: 'Extended Time (1.5x) for Reading Comprehension', checked: true },
    { id: 2, text: 'Noise-Canceling Ear Defenders during Assembly', checked: true },
    { id: 3, text: 'Slant Board & Ergonomic Grip Pencil', checked: true },
    { id: 4, text: 'Frequent Movement Breaks (every 20 mins)', checked: false }
  ]);
  const [sensoryTimerActive, setSensoryTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(300);
  const [savedMsg, setSavedMsg] = useState('');

  const toggleAccommodation = (id: number) => {
    setAccommodations(prev => prev.map(a => a.id === id ? { ...a, checked: !a.checked } : a));
  };

  const handleSaveLesson = () => {
    setSavedMsg('Lesson plan and accommodations saved successfully!');
    setTimeout(() => setSavedMsg(''), 4000);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <span className="px-2.5 py-0.5 bg-rose-100 text-rose-800 text-[10px] font-bold rounded-full uppercase tracking-wider">
            ClassDojo & Goalbook Benchmark
          </span>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">Teacher Classroom & Inclusive Lesson Workspace</h2>
          <p className="text-xs text-slate-500">Adaptive lesson planning, classroom accommodations matrix, and student engagement</p>
        </div>

        <button 
          onClick={handleSaveLesson}
          className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow transition flex items-center gap-1.5"
        >
          <Sparkles className="w-4 h-4 text-amber-300" />
          <span>Save Lesson Plan</span>
        </button>
      </div>

      {savedMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          {savedMsg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Adaptive Lesson Plan Generator */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-rose-600" />
            Today's Adaptive Lesson Plan
          </h3>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700">Lesson Topic</label>
            <input 
              type="text"
              value={lessonTopic}
              onChange={(e) => setLessonTopic(e.target.value)}
              className="w-full px-3 py-2 border rounded-xl text-xs font-bold focus:ring-2 focus:ring-rose-500"
            />
          </div>

          <div className="bg-white p-3 rounded-lg border border-slate-200 text-xs space-y-2">
            <div className="font-bold text-slate-900">Current Unit: {lessonTopic}</div>
            <p className="text-slate-600">Tactile soil & water experiment with AAC vocabulary prompts (Wet, Cold, Soft, Heavy).</p>
            <div className="flex gap-2">
              <span className="px-2 py-0.5 bg-rose-50 text-rose-700 text-[10px] font-bold rounded">Visual Supports</span>
              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded">AAC Board Ready</span>
            </div>
          </div>
        </div>

        {/* Accommodations Checklist */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
            <CheckSquare className="w-4 h-4 text-emerald-600" />
            Accommodations Checklist
          </h3>

          <ul className="space-y-2 text-xs text-slate-700">
            {accommodations.map(acc => (
              <li key={acc.id} className="flex items-center gap-2 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer" onClick={() => toggleAccommodation(acc.id)}>
                <input 
                  type="checkbox" 
                  checked={acc.checked} 
                  onChange={() => toggleAccommodation(acc.id)}
                  className="rounded text-rose-600 focus:ring-rose-500" 
                />
                <span className={acc.checked ? 'font-bold text-slate-900' : 'text-slate-500 line-through'}>{acc.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
